/**
 * QTG Application Role Constants
 *
 * These roles are defined in Azure AD and included in the JWT bearer token.
 * Use these constants throughout the application for role-based access control.
 */

export const QTG_ROLES = {
  /** Full administrative access - can create, edit, delete, and manage all resources */
  ADMIN: "QTG-LV-Admins",

  /** Read-only administrative access - can view all resources but cannot modify */
  READ_ONLY_ADMIN: "QTG-LV-ReadOnly-Admins",

  /** Internal users - GrECo employees with standard access */
  INTERNAL: "QTG-LV-Internal",

  /** External users - Partners or clients with limited access */
  EXTERNAL: "QTG-LV-External",
} as const;

// Type for role values
export type QTGRole = (typeof QTG_ROLES)[keyof typeof QTG_ROLES];

// Helper functions for role checking
export const RolePermissions = {
  /** Roles that can edit offers and quotes */
  canEdit: () => [QTG_ROLES.ADMIN, QTG_ROLES.INTERNAL],

  /** Roles that can delete offers */
  canDelete: () => [QTG_ROLES.ADMIN],

  /** Roles that can view all data */
  canViewAll: () => [
    QTG_ROLES.ADMIN,
    QTG_ROLES.READ_ONLY_ADMIN,
    QTG_ROLES.INTERNAL,
  ],

  /** Roles that can view settings page */
  canViewSettings: () => [QTG_ROLES.ADMIN, QTG_ROLES.READ_ONLY_ADMIN],

  /** Roles that can edit and manage settings */
  canEditSettings: () => [QTG_ROLES.ADMIN],

  /** Roles that can invalidate offers */
  canInvalidateOffers: () => [QTG_ROLES.ADMIN, QTG_ROLES.INTERNAL],

  /** Roles that can mark/unmark offers as error entries */
  canMarkOffersAsError: () => [QTG_ROLES.ADMIN],

  /** Roles that can choose to list all, valid or error entries (others always get valid entries) */
  canChooseOffersShowMode: () => [QTG_ROLES.ADMIN, QTG_ROLES.READ_ONLY_ADMIN],

  /** Roles that have read-only access */
  isReadOnly: () => [QTG_ROLES.READ_ONLY_ADMIN, QTG_ROLES.EXTERNAL],
};

/**
 * Get a human-readable display name for a role
 */
export function getRoleDisplayName(role: string): string {
  switch (role) {
    case QTG_ROLES.ADMIN:
      return "Administrator";
    case QTG_ROLES.READ_ONLY_ADMIN:
      return "Read-Only Administrator";
    case QTG_ROLES.INTERNAL:
      return "Internal User";
    case QTG_ROLES.EXTERNAL:
      return "External User";
    default:
      return role;
  }
}

/**
 * Get a description for a role
 */
export function getRoleDescription(role: string): string {
  switch (role) {
    case QTG_ROLES.ADMIN:
      return "Full access to create, edit, delete, and manage all resources. Can modify settings.";
    case QTG_ROLES.READ_ONLY_ADMIN:
      return "View all resources and settings without modification rights";
    case QTG_ROLES.INTERNAL:
      return "Standard access for GrECo employees - can create and edit offers";
    case QTG_ROLES.EXTERNAL:
      return "Limited access for partners and clients - read-only";
    default:
      return "Unknown role";
  }
}
